import Web3 from 'web3';
import { AbiItem } from 'web3-utils';
import { useTranslation } from 'react-i18next';
import atomicSwapContractAbi from 'abis/atomicSwapContractAbi.json';
import erc1404TokenAbi from 'abis/erc1404TokenAbi.json';
import { BsSwal } from 'atoms';
import { ExchangeOfferDetailOffer, ExchangeOfferDetailOrder } from 'services/apollo/exchange';
import {
  AtomicSwapStatus,
  useUpdateOrderStatusMutation,
  useAcceptBlockchainSwapMutation,
  BlockchainProtocol,
} from 'services/apollo';
import { forceBlockchain, prepareBlockchainData, toTokenPowerOf } from 'lib/helpers';

const useAtomicSwap = (
  order: ExchangeOfferDetailOrder,
  offer: ExchangeOfferDetailOffer,
  walletAddress: string,
  setAtomicSwapCurrentStatus: (atomicSwapStatus: AtomicSwapStatus) => void,
  setIsApproveLoading: (isApproveLoading: boolean) => void,
  setIsSwapLoading: (isSwapLoading: boolean) => void,
  isSell: boolean,
  isBuy: boolean,
  isTestnetEnabled: boolean,
  blockchainProtocol: BlockchainProtocol,
): [
  (investorWalletAddress: string) => Promise<boolean>,
  (tokenAmount: number, tokenContractAddress: string, isOrderCreator: boolean) => void,
  () => void,
  () => void,
] => {
  const { t } = useTranslation();
  const [updateOrderStatus] = useUpdateOrderStatusMutation();
  const [acceptBlockchainSwap] = useAcceptBlockchainSwapMutation();
  const { chainID, nodeProvider, blockchainExplorer, swapContractAddress } = prepareBlockchainData(
    blockchainProtocol.blockchain,
    isTestnetEnabled,
  );

  const isInvestorWhiteListed = async (investorWalletAddress: string): Promise<boolean> => {
    if (investorWalletAddress !== '') {
      const web3 = new Web3(new Web3.providers.HttpProvider(nodeProvider));
      if (order.shareType.ethereumContractAddress && order.atomicSwapTokenAddressAcceptable) {
        const contract = new web3.eth.Contract(erc1404TokenAbi as AbiItem[], order.shareType.ethereumContractAddress);
        const isWalletWhitelisted = await contract.methods.getKYCData(investorWalletAddress).call();
        if (isWalletWhitelisted['0'] === '1' && isWalletWhitelisted['1'] === '1') {
          return true;
        }
      }
    }
    return false;
  };

  const prepareRawTransaction = async (to: string, data: string, web3: Web3) => {
    const transactionCount = await web3.eth.getTransactionCount(walletAddress, 'pending');
    return {
      from: walletAddress,
      to,
      value: 0x0,
      data,
      nonce: parseInt(web3.utils.toHex(transactionCount), 16),
    };
  };

  const prepareApproveTransaction = async (web3: Web3, tokenAmount: number, tokenContractAddress: string) => {
    const contract = new web3.eth.Contract(erc1404TokenAbi as AbiItem[], tokenContractAddress);
    const parsedTokenAmount = await toTokenPowerOf(contract, tokenAmount);
    const approveMethodCall = contract.methods.approve(swapContractAddress, parsedTokenAmount).encodeABI();
    return approveMethodCall;
  };

  const handleApproveTransfers = async (tokenAmount: number, tokenContractAddress: string, isOrderCreator: boolean) => {
    if (walletAddress === '') {
      return BsSwal.fire({
        title: t('useAtomicSwap-approval-PopUp-fail-title'),
        icon: 'error',
      });
    }
    const isBlockchainForced = await forceBlockchain(chainID);
    if (isBlockchainForced) {
      setIsApproveLoading(true);
      const web3 = new Web3(window.ethereum);
      try {
        const preparedApproveData = await prepareApproveTransaction(web3, tokenAmount, tokenContractAddress);
        const rawTransaction = await prepareRawTransaction(tokenContractAddress, preparedApproveData, web3);
        const transactionReceipt = await web3.eth.sendTransaction(rawTransaction, (error) => {
          if (error) {
            BsSwal.fire({
              title: t('useAtomicSwap-approval-PopUp-fail-title'),
              text: error.message,
              icon: 'error',
            });
            setIsApproveLoading(false);
          }
        });

        if (transactionReceipt) {
          const variables = {
            orderId: order.ID,
            atomicSwapCurrentStatus:
              (isOrderCreator && isSell) || (!isOrderCreator && isBuy)
                ? AtomicSwapStatus.SellerCommitted
                : AtomicSwapStatus.BuyerCommitted,
          };
          await updateOrderStatus({ variables });
          setAtomicSwapCurrentStatus(variables.atomicSwapCurrentStatus);
          BsSwal.fire({
            title: t('useAtomicSwap-approval-PopUp-success-title'),
            text: t('useAtomicSwap-approval-PopUp-success-description'),
            icon: 'success',
            footer: `<a target="_blank" href="${blockchainExplorer}tx/${transactionReceipt.transactionHash}">
            ${t('useAtomicSwap-approval-PopUp-success-footer')}</a>`,
          });
          setIsApproveLoading(false);
        }
      } catch (error) {
        BsSwal.fire({
          title: t('useAtomicSwap-approval-PopUp-fail-title'),
          text: 'error',
          icon: 'error',
        });
        setIsApproveLoading(false);
      }
    } else {
      BsSwal.fire({
        title: t('useAtomicSwap-approval-PopUp-fail-title'),
        icon: 'error',
      });
    }
  };

  const prepareOpenTransaction = async (web3: Web3) => {
    if (
      offer &&
      offer.atomicSwapSecret &&
      order.shareType.ethereumContractAddress &&
      order.atomicSwapTokenAddressAcceptable
    ) {
      const contract = new web3.eth.Contract(atomicSwapContractAbi as AbiItem[], swapContractAddress);
      const sharesContract = new web3.eth.Contract(
        erc1404TokenAbi as AbiItem[],
        order.shareType.ethereumContractAddress,
      );
      const currencyContract = new web3.eth.Contract(
        erc1404TokenAbi as AbiItem[],
        order.atomicSwapTokenAddressAcceptable,
      );
      const sharesTokenAmount = await toTokenPowerOf(sharesContract, offer.sharesPartial);
      const currencyTokenAmount = await toTokenPowerOf(currencyContract, offer.rateFrom);

      const _swapNumber = web3.utils.toHex(offer.atomicSwapSecret);
      const _executor = offer.atomicBuyerPublicKey;
      const _openingToken = isSell ? order.shareType.ethereumContractAddress : order.atomicSwapTokenAddressAcceptable;
      const tokensToOpen = isSell ? sharesTokenAmount : currencyTokenAmount;
      const _tokensToOpen = web3.utils.toHex(tokensToOpen);
      const _closingToken = isSell ? order.atomicSwapTokenAddressAcceptable : order.shareType.ethereumContractAddress;
      const tokenToClose = isSell ? currencyTokenAmount : sharesTokenAmount;
      const _tokensToClose = web3.utils.toHex(tokenToClose);
      const _expiry = web3.utils.toHex(Math.floor(new Date(order.dateTo).getTime() / 1000));

      const openMethodCall = contract.methods
        .open(_swapNumber, _executor, _openingToken, _tokensToOpen, _closingToken, _tokensToClose, _expiry)
        .encodeABI();

      return openMethodCall;
    }
    return null;
  };

  const handleOpenSwap = async () => {
    if (walletAddress === '') {
      return BsSwal.fire({
        title: t('useAtomicSwap-approval-PopUp-fail-title'),
        icon: 'error',
      });
    }
    const isBlockchainForced = await forceBlockchain(chainID);
    if (isBlockchainForced) {
      setIsSwapLoading(true);
      const web3 = new Web3(window.ethereum);
      try {
        const preparedOpenData = await prepareOpenTransaction(web3);
        const rawTransaction = await prepareRawTransaction(swapContractAddress, preparedOpenData, web3);
        const transactionReceipt = await web3.eth.sendTransaction(rawTransaction, (error) => {
          if (error) {
            BsSwal.fire({
              title: t('useAtomicSwap-openSwap-PopUp-fail-title'),
              text: error.message,
              icon: 'error',
            });
            setIsSwapLoading(false);
          }
        });
        if (transactionReceipt) {
          await acceptBlockchainSwap({
            variables: { orderID: order.ID, walletAddress },
          });
          if (isSell) {
            setAtomicSwapCurrentStatus(AtomicSwapStatus.SellerSent);
          }
          if (isBuy) {
            setAtomicSwapCurrentStatus(AtomicSwapStatus.BuyerCompleted);
          }
          BsSwal.fire({
            title: t('useAtomicSwap-openSwap-PopUp-success-title'),
            text: t('useAtomicSwap-openSwap-PopUp-success-text'),
            icon: 'success',
            footer: `<a target="_blank" href="${blockchainExplorer}tx/${transactionReceipt.transactionHash}">
            ${t('useAtomicSwap-openSwap-PopUp-success-footer')}</a>`,
          });
          setIsSwapLoading(false);
        }
      } catch (error) {
        BsSwal.fire({
          title: t('useAtomicSwap-openSwap-PopUp-fail-title'),
          text: 'error',
          icon: 'error',
        });
        setIsSwapLoading(false);
      }
    } else {
      BsSwal.fire({
        title: t('useAtomicSwap-approval-PopUp-fail-title'),
        icon: 'error',
      });
    }
  };

  const prepareCloseTransaction = (web3: Web3) => {
    if (offer && offer.atomicBuyerPublicKey && offer.atomicSwapSecret && order.atomicSwapSharesWallet) {
      const contract = new web3.eth.Contract(atomicSwapContractAbi as AbiItem[], swapContractAddress);
      const _originator = order.atomicSwapSharesWallet.publicKey;
      const _swapNumber = web3.utils.toHex(offer.atomicSwapSecret);

      const closeMethodCall = contract.methods.close(_originator, _swapNumber).encodeABI();

      return closeMethodCall;
    }
    return null;
  };

  const handleCloseSwap = async () => {
    if (walletAddress === '') {
      return BsSwal.fire({
        title: t('useAtomicSwap-approval-PopUp-fail-title'),
        icon: 'error',
      });
    }
    const isBlockchainForced = await forceBlockchain(chainID);
    if (isBlockchainForced) {
      setIsSwapLoading(true);
      const web3 = new Web3(window.ethereum);
      try {
        const preparedCloseData = prepareCloseTransaction(web3);
        const rawTransaction = await prepareRawTransaction(swapContractAddress, preparedCloseData, web3);
        const transactionReceipt = await web3.eth.sendTransaction(rawTransaction, (error) => {
          if (error) {
            BsSwal.fire({
              title: t('useAtomicSwap-closeSwap-PopUp-fail-title'),
              text: error.message,
              icon: 'error',
            });
            setIsSwapLoading(false);
          }
        });
        if (transactionReceipt) {
          await acceptBlockchainSwap({
            variables: { orderID: order.ID, walletAddress },
          });
          if (isSell) {
            setAtomicSwapCurrentStatus(AtomicSwapStatus.BuyerCompleted);
          }
          if (isBuy) {
            setAtomicSwapCurrentStatus(AtomicSwapStatus.SellerSent);
          }
          BsSwal.fire({
            title: t('useAtomicSwap-closeSwap-PopUp-success-title'),
            icon: 'success',
            footer: `<a target="_blank" href="${blockchainExplorer}tx/${transactionReceipt.transactionHash}">
              ${t('useAtomicSwap-closeSwap-PopUp-success-footer')}</a>`,
          });
          setIsSwapLoading(false);
        }
      } catch (error) {
        BsSwal.fire({
          title: t('useAtomicSwap-closeSwap-PopUp-fail-title'),
          text: 'error',
          icon: 'error',
        });
        setIsSwapLoading(false);
      }
    } else {
      BsSwal.fire({
        title: t('useAtomicSwap-approval-PopUp-fail-title'),
        icon: 'error',
      });
    }
  };

  return [isInvestorWhiteListed, handleApproveTransfers, handleOpenSwap, handleCloseSwap];
};

export default useAtomicSwap;
